/**
 * Shared section labels and structure for displaying clinical notes by type.
 * Used by the Medical Record timeline "Show Note", the note detail modal, and add-note modals
 * so that add modal, list expand, and detail view all match.
 */
import type { ClinicalNote } from './types';

export type NoteType = ClinicalNote['noteType'];

export interface NoteTypeSectionConfig {
  heading: string;
  sections: { label: string; key: keyof NonNullable<ClinicalNote['soapNote']> }[];
}

/** Section configs for note types that have structured SOAP/content. Others fall back to DEFAULT_CONFIG. */
export const NOTE_TYPE_SECTIONS: Partial<Record<NoteType, NoteTypeSectionConfig>> = {
  'Progress Note': {
    heading: 'Progress note',
    sections: [
      { label: 'Presenting Problem / Reason for Visit', key: 'subjective' },
      { label: 'Client Response / Progress', key: 'objective' },
      { label: 'Assessment', key: 'assessment' },
      { label: 'Treatment Plan / Next Steps', key: 'plan' },
    ],
  },
  'Intake Note': {
    heading: 'Intake note',
    sections: [
      { label: 'Presenting concerns / Reason for seeking treatment', key: 'subjective' },
      { label: 'History and background', key: 'objective' },
      { label: 'Diagnostic impressions', key: 'assessment' },
      { label: 'Treatment recommendations and plan', key: 'plan' },
    ],
  },
  'Chart Note': {
    heading: 'Chart note',
    sections: [
      { label: 'Content', key: 'subjective' },
    ],
  },
  'Psychotherapy Note': {
    heading: 'Psychotherapy note',
    sections: [
      { label: 'Focus / Subjective', key: 'subjective' },
      { label: 'Observations', key: 'objective' },
      { label: 'Clinical impression', key: 'assessment' },
      { label: 'Plan', key: 'plan' },
    ],
  },
  'Administrative Note': {
    heading: 'Administrative note',
    sections: [
      { label: 'Summary', key: 'subjective' },
      { label: 'Details', key: 'objective' },
      { label: 'Assessment / Notes', key: 'assessment' },
      { label: 'Next steps / Action items', key: 'plan' },
    ],
  },
  'Risk Assessment': {
    heading: 'Risk assessment',
    sections: [
      { label: 'Presenting concerns / Triggers', key: 'subjective' },
      { label: 'Risk factors and protective factors', key: 'objective' },
      { label: 'Clinical assessment and level of risk', key: 'assessment' },
      { label: 'Recommendations and follow-up', key: 'plan' },
    ],
  },
  'Safety Plan': {
    heading: 'Safety plan',
    sections: [
      { label: 'Warning signs', key: 'subjective' },
      { label: 'Coping strategies and supports', key: 'objective' },
      { label: 'Safe environment and means restriction', key: 'assessment' },
      { label: 'Crisis contacts and next steps', key: 'plan' },
    ],
  },
  'Family Therapy Progress Note': {
    heading: 'Family therapy progress note',
    sections: [
      { label: 'Family members present / Presenting concerns', key: 'subjective' },
      { label: 'Family dynamics and interaction patterns', key: 'objective' },
      { label: 'Progress toward treatment goals', key: 'assessment' },
      { label: 'Plan / Next steps', key: 'plan' },
    ],
  },
  'Couples Therapy Progress Note': {
    heading: 'Couples therapy progress note',
    sections: [
      { label: 'Attendance / Topics discussed', key: 'subjective' },
      { label: 'Couple interaction and communication', key: 'objective' },
      { label: 'Progress on relationship goals', key: 'assessment' },
      { label: 'Plan / Next steps', key: 'plan' },
    ],
  },
  'IOP Group Session Note': {
    heading: 'IOP group session note',
    sections: [
      { label: 'Module / topic / Client self-report', key: 'subjective' },
      { label: 'Participation and skills practiced', key: 'objective' },
      { label: 'Risk level and step-down readiness', key: 'assessment' },
      { label: 'Plan / Coordination', key: 'plan' },
    ],
  },
  'Group Therapy Progress Note': {
    heading: 'Group therapy progress note',
    sections: [
      { label: 'Group topic / theme', key: 'subjective' },
      { label: 'Group process and observations', key: 'objective' },
      { label: 'Individual participation notes', key: 'assessment' },
      { label: 'Plan / Next steps', key: 'plan' },
    ],
  },
  // Medical Record filter types (same section layout as canonical type where applicable)
  'Progress Notes': {
    heading: 'Progress note',
    sections: [
      { label: 'Presenting Problem / Reason for Visit', key: 'subjective' },
      { label: 'Client Response / Progress', key: 'objective' },
      { label: 'Assessment', key: 'assessment' },
      { label: 'Treatment Plan / Next Steps', key: 'plan' },
    ],
  },
  'Chart Notes': {
    heading: 'Chart note',
    sections: [
      { label: 'Content', key: 'subjective' },
    ],
  },
  'Psychotherapy Notes': {
    heading: 'Psychotherapy note',
    sections: [
      { label: 'Focus / Subjective', key: 'subjective' },
      { label: 'Observations', key: 'objective' },
      { label: 'Clinical impression', key: 'assessment' },
      { label: 'Plan', key: 'plan' },
    ],
  },
  'Case Review': {
    heading: 'Case review',
    sections: [
      { label: 'Summary', key: 'subjective' },
      { label: 'Review details', key: 'objective' },
      { label: 'Assessment', key: 'assessment' },
      { label: 'Plan / Next steps', key: 'plan' },
    ],
  },
  'Collateral Contact Note': {
    heading: 'Collateral contact note',
    sections: [
      { label: 'Context / Summary', key: 'subjective' },
      { label: 'Contact details', key: 'objective' },
      { label: 'Assessment', key: 'assessment' },
      { label: 'Follow-up', key: 'plan' },
    ],
  },
  'Transfer Request': {
    heading: 'Transfer request',
    sections: [
      { label: 'Transfer reasons and explanation', key: 'subjective' },
      { label: 'Matching preferences', key: 'objective' },
      { label: 'Status and assignment', key: 'assessment' },
      { label: 'Outcome / next steps', key: 'plan' },
    ],
  },
};

/** Default when note type is missing or unknown. */
const DEFAULT_CONFIG: NoteTypeSectionConfig = {
  heading: 'Chart note',
  sections: [{ label: 'Content', key: 'subjective' }],
};

export function getSectionsForNoteType(noteType: NoteType | undefined): NoteTypeSectionConfig {
  if (!noteType) return DEFAULT_CONFIG;
  return NOTE_TYPE_SECTIONS[noteType] ?? DEFAULT_CONFIG;
}
